import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Mail, Users, Globe, CheckCircle2, AlertCircle } from 'lucide-react';

const Card = ({ title, right, children }) => (
  <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3l7 3v6c0 5-3.5 9-7 9s-7-4-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  </svg>
);

function DistributionPlanner() {
  const [selectedOutlets, setSelectedOutlets] = useState(new Set(['WRFG', 'Pitchfork', 'IssueWire', 'PRLog']));
  const [searchTerm, setSearchTerm] = useState('');
  const [personalizationData, setPersonalizationData] = useState({
    emailSubject: '',
    fromEmail: '',
    pitchIntro: ''
  });

  const outlets = [
    { name: "WRFG (Atlanta)", type: "Radio", reach: "Regional", category: "Local" },
    { name: "Pitchfork", type: "Blog", reach: "National", category: "Music" },
    { name: "IssueWire", type: "PR Platform", reach: "Syndication", category: "General" },
    { name: "PRLog", type: "PR Platform", reach: "Syndication", category: "General" },
    { name: "WUOG (UGA)", type: "Radio", reach: "College", category: "Educational" },
    { name: "Obscure Sound", type: "Blog", reach: "Indie", category: "Music" },
    { name: "TechCrunch", type: "Blog", reach: "National", category: "Technology" },
    { name: "Local News 11", type: "TV/News", reach: "Regional", category: "News" },
  ];

  const filterTags = [
    "PR Platforms", "Blogs", "Radio (College)", "TV/News", "Video Hosts", 
    "Regional: Southeast", "Music", "Books", "Tech"
  ];

  const filteredOutlets = outlets?.filter(outlet =>
    outlet?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    outlet?.type?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleOutletToggle = (outletName) => {
    const newSelected = new Set(selectedOutlets);
    if (newSelected?.has(outletName)) {
      newSelected?.delete(outletName);
    } else {
      newSelected?.add(outletName);
    }
    setSelectedOutlets(newSelected);
  };

  const handlePersonalizationChange = (field, value) => {
    setPersonalizationData(prev => ({ ...prev, [field]: value }));
  };

  const selectedCount = selectedOutlets?.size;
  const estimatedReach = selectedCount * 16000; // Rough estimate

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Outlet Targeting" right={
          <button className="text-sm text-blue-600 hover:text-blue-800 underline">
            Import Contacts
          </button>
        }>
          {/* Filter Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {filterTags?.map((tag, index) => (
              <motion.span 
                key={index}
                whileHover={{ scale: 1.05 }}
                className="px-3 py-1 rounded-full bg-gray-100 text-xs cursor-pointer hover:bg-gray-200 transition"
              >
                {tag}
              </motion.span>
            ))}
          </div>

          {/* Search and Filter */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input 
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-black" 
                placeholder="Search outlets (e.g., WRFG, Pitchfork, IssueWire)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
              />
            </div>
            <button className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
              <Filter size={16}/>
              Filters
            </button>
          </div>

          {/* Outlets Grid */}
          <div className="grid md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
            {filteredOutlets?.map((outlet, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`border rounded-2xl p-4 flex items-center justify-between cursor-pointer transition ${
                  selectedOutlets?.has(outlet?.name) 
                    ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleOutletToggle(outlet?.name)}
              >
                <div>
                  <p className="font-medium">{outlet?.name}</p>
                  <p className="text-xs text-gray-500">{outlet?.type} • {outlet?.reach}</p>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 mt-1 inline-block">
                    {outlet?.category}
                  </span>
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input 
                    type="checkbox" 
                    className="rounded" 
                    checked={selectedOutlets?.has(outlet?.name)}
                    onChange={() => handleOutletToggle(outlet?.name)}
                  />
                  <span>Select</span>
                </label>
              </motion.div>
            ))}
          </div>
        </Card>

        <Card title="Message Personalization">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <label className="block">
              <span className="text-xs text-gray-600">Email Subject</span>
              <input 
                className="mt-1 w-full rounded-xl border border-gray-200 p-2 focus:ring-2 focus:ring-black" 
                placeholder="Atlanta's PenGame unveils StoryScape XR update"
                value={personalizationData?.emailSubject}
                onChange={(e) => handlePersonalizationChange('emailSubject', e?.target?.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-600">From / Reply‑To</span>
              <input 
                className="mt-1 w-full rounded-xl border border-gray-200 p-2 focus:ring-2 focus:ring-black" 
                placeholder="press@pengamepublishing.com"
                value={personalizationData?.fromEmail}
                onChange={(e) => handlePersonalizationChange('fromEmail', e?.target?.value)}
              />
            </label>
            <label className="block col-span-2">
              <span className="text-xs text-gray-600">Personalized Pitch Intro</span>
              <textarea 
                className="mt-1 w-full rounded-xl border border-gray-200 p-2 focus:ring-2 focus:ring-black" 
                rows={4} 
                placeholder="Hi {FirstName}, quick tip for your {Beat} readers..."
                value={personalizationData?.pitchIntro}
                onChange={(e) => handlePersonalizationChange('pitchIntro', e?.target?.value)}
              />
            </label>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-xs text-blue-700">
              💡 <strong>AI Tip:</strong> Use variables like {"{FirstName}"}, {"{OutletName}"}, and {"{Beat}"} for automatic personalization
            </p>
          </div>
        </Card>

        <div className="flex gap-3">
          <button className="px-6 py-3 rounded-2xl bg-black text-white font-medium hover:bg-gray-800 transition">
            Send Test Email
          </button>
          <button className="px-6 py-3 rounded-2xl border border-gray-200 font-medium hover:bg-gray-50 transition">
            Schedule Distribution
          </button>
          <button className="px-6 py-3 rounded-2xl bg-green-600 text-white font-medium hover:bg-green-700 transition">
            Send Now
          </button>
        </div>
      </div>
      <div className="space-y-6">
        <Card title="Compliance & Delivery">
          <ul className="text-sm space-y-3">
            <li className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <Mail size={14} className="text-green-600"/>
              </div>
              <div>
                <p className="font-medium">Auto-unsubscribe footer</p>
                <p className="text-xs text-gray-500">GDPR & CAN-SPAM compliant</p>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <ShieldIcon />
              </div>
              <div>
                <p className="font-medium">SPF/DKIM alignment</p>
                <p className="text-xs text-gray-500">Email authentication verified</p>
              </div>
            </li>
            <li className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                <Globe size={14} className="text-purple-600"/>
              </div>
              <div>
                <p className="font-medium">Rate limiting & warm-up</p>
                <p className="text-xs text-gray-500">Optimized delivery timing</p>
              </div>
            </li>
          </ul>
        </Card>

        <Card title="Campaign Summary">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Selected Outlets:</span>
              <span className="font-bold text-lg">{selectedCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Est. Total Reach:</span>
              <span className="font-bold text-lg">{(estimatedReach / 1000)?.toFixed(1)}K</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Delivery Time:</span>
              <span className="font-medium">~{Math.ceil(selectedCount / 10)} hours</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success Rate:</span>
              <span className="font-medium text-green-600">94.2%</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <CheckCircle2 size={12} className="text-green-500" />
              Ready to send • All checks passed
            </div>
          </div>
        </Card>

        <Card title="Smart Recommendations">
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle size={14} className="text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium">Best send time</p>
                <p className="text-xs text-gray-500">Tuesday 10 AM EST for optimal opens</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Users size={14} className="text-blue-500 mt-0.5" />
              <div>
                <p className="font-medium">Additional outlets</p>
                <p className="text-xs text-gray-500">Consider adding 3 tech blogs for broader reach</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default DistributionPlanner;